import { cn } from "@/lib/utils";

import { LOGOS } from "./data";

export interface VsLogo {
  src: string;
  alt: string;
  width: number;
  height: number;
}

interface VsLogosProps {
  className?: string;
  /** Override the default founder logo set (e.g. on the Big Pitch sub-page). */
  logos?: readonly VsLogo[];
  /** Override the eyebrow label above the logo row. */
  label?: string;
}

/**
 * "Featuring founders from:" logo strip. The logo row auto-scrolls to the
 * left as a seamless marquee with edge fades on both sides. The animation
 * pauses on hover (desktop) and is disabled for users who prefer reduced
 * motion (the row falls back to a static, centered layout).
 */
export function VsLogos({
  className,
  logos = LOGOS,
  label = "Featuring founders from:",
}: VsLogosProps) {
  return (
    <section
      aria-label="Featured founder brands"
      className={cn("relative bg-[#0d0d0d]", className)}
    >
      {/*
        Inline keyframes keep the marquee self-contained to this file. The
        translate is -50% because the inner row renders the logo set twice,
        so a half-loop wraps perfectly to the start.
      */}
      <style>{`
        @keyframes vs-logos-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>

      <div className="relative mx-auto flex max-w-[1440px] flex-col gap-4 px-6 pt-2 pb-8 md:gap-6 md:px-10 md:pt-3 md:pb-10 lg:px-[150px] lg:pt-4 lg:pb-12">
        {/*
          NOTE: text-align, font-size, and color are set inline because the
          `cn()` helper uses tailwind-merge, which collapses arbitrary `text-[…]`
          classes that share its `text-` prefix (it can't distinguish color vs.
          font-size for arbitrary values). Inline styles sidestep the merge.
        */}
        <p
          style={{
            textAlign: "center",
            color: "#b2b2b2",
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
          }}
          className={cn(
            "text-[14px] leading-[1.4] tracking-[-0.01em]",
            "md:text-[16px] md:leading-[24px] md:tracking-[-0.16px]"
          )}
        >
          {label}
        </p>

        <div
          className={cn(
            "relative -mx-6 overflow-hidden md:mx-0",
            "[mask-image:linear-gradient(to_right,transparent_0,#0d0d0d_60px,#0d0d0d_calc(100%-60px),transparent_100%)]",
            "[-webkit-mask-image:linear-gradient(to_right,transparent_0,#0d0d0d_60px,#0d0d0d_calc(100%-60px),transparent_100%)]",
            "motion-reduce:flex motion-reduce:justify-center"
          )}
        >
          <ul
            aria-hidden={false}
            className={cn(
              "flex w-max items-center gap-10 pr-10",
              "[animation:vs-logos-marquee_55s_linear_infinite]",
              "md:gap-16 md:pr-16 md:[animation-duration:60s]",
              "hover:[animation-play-state:paused]",
              "motion-reduce:w-auto motion-reduce:[animation:none] motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:pr-0"
            )}
          >
            {logos.map((logo) => (
              <li
                key={`a-${logo.alt}-${logo.src}`}
                className="flex shrink-0 items-center"
                style={{ height: 32 }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  className="h-auto w-auto object-contain opacity-90"
                  style={{ maxHeight: logo.height }}
                />
              </li>
            ))}
            {/*
              Second copy is purely visual to fill the remaining 50% of the
              translate so the loop is seamless. Hidden from assistive tech
              to avoid duplicate logo announcements.
            */}
            {logos.map((logo) => (
              <li
                key={`b-${logo.alt}-${logo.src}`}
                aria-hidden="true"
                className="flex shrink-0 items-center motion-reduce:hidden"
                style={{ height: 32 }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={logo.src}
                  alt=""
                  width={logo.width}
                  height={logo.height}
                  className="h-auto w-auto object-contain opacity-90"
                  style={{ maxHeight: logo.height }}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
